import React, { useState, useRef } from 'react';
import './PhoneInputField.css';

interface CountryCode {
    code: string;
    dialCode: string;
    name: string;
    flag: string;
}

const countryCodes: CountryCode[] = [
    { code: 'CL', dialCode: '+56', name: 'Chile', flag: '🇨🇱' },
    { code: 'AR', dialCode: '+54', name: 'Argentina', flag: '🇦🇷' },
    { code: 'PE', dialCode: '+51', name: 'Perú', flag: '🇵🇪' },
    { code: 'CO', dialCode: '+57', name: 'Colombia', flag: '🇨🇴' },
    { code: 'MX', dialCode: '+52', name: 'México', flag: '🇲🇽' },
    { code: 'ES', dialCode: '+34', name: 'España', flag: '🇪🇸' },
    { code: 'US', dialCode: '+1', name: 'Estados Unidos', flag: '🇺🇸' },
    { code: 'BR', dialCode: '+55', name: 'Brasil', flag: '🇧🇷' },
    { code: 'UY', dialCode: '+598', name: 'Uruguay', flag: '🇺🇾' },
    { code: 'PY', dialCode: '+595', name: 'Paraguay', flag: '🇵🇾' },
    { code: 'BO', dialCode: '+591', name: 'Bolivia', flag: '🇧🇴' },
    { code: 'EC', dialCode: '+593', name: 'Ecuador', flag: '🇪🇨' },
    { code: 'VE', dialCode: '+58', name: 'Venezuela', flag: '🇻🇪' },
    { code: 'PA', dialCode: '+507', name: 'Panamá', flag: '🇵🇦' },
    { code: 'CR', dialCode: '+506', name: 'Costa Rica', flag: '🇨🇷' },
    { code: 'GT', dialCode: '+502', name: 'Guatemala', flag: '🇬🇹' },
    { code: 'HN', dialCode: '+504', name: 'Honduras', flag: '🇭🇳' },
    { code: 'NI', dialCode: '+505', name: 'Nicaragua', flag: '🇳🇮' },
    { code: 'SV', dialCode: '+503', name: 'El Salvador', flag: '🇸🇻' },
    { code: 'DO', dialCode: '+1', name: 'República Dominicana', flag: '🇩🇴' },
];

interface PhoneInputFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    error?: string;
    required?: boolean;
    placeholder?: string;
    backgroundColor?: string;
    defaultCountry?: string; // Código de país por defecto (ej: 'CL')
}

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
    label,
    value,
    onChange,
    onFocus,
    error,
    required = false,
    placeholder = 'Ingrese su número de teléfono',
    backgroundColor = '#ffffff',
    defaultCountry = 'CL'
}) => {
    const parseInitialValue = (val: string): { country: CountryCode; number: string } => {
        if (!val || val.trim() === '') {
            return {
                country: countryCodes.find(c => c.code === defaultCountry) || countryCodes[0],
                number: ''
            };
        }

        // Si ya viene con código de país
        if (val.startsWith('+')) {
            const parts = val.split(' ');
            const dialCode = parts[0];
            const number = parts.slice(1).join(' ');

            // Buscar el país por código
            const country = countryCodes.find(c => c.dialCode === dialCode);
            if (country) {
                return { country, number };
            }
        }

        // Si no tiene código, usar el país por defecto
        return {
            country: countryCodes.find(c => c.code === defaultCountry) || countryCodes[0],
            number: val
        };
    };

    const initialData = parseInitialValue(value);
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(initialData.country);
    const [showDropdown, setShowDropdown] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(initialData.number);

    const handleCountrySelect = (country: CountryCode) => {
        setSelectedCountry(country);
        setShowDropdown(false);
        // Actualizar el valor completo con el nuevo código
        const fullNumber = phoneNumber ? `${country.dialCode} ${phoneNumber}` : country.dialCode;
        onChange(fullNumber);
    };

    const handlePhoneChange = (number: string) => {
        // Solo permitir números y espacios
        const cleaned = number.replace(/[^\d\s]/g, '');
        setPhoneNumber(cleaned);
        // Actualizar el valor completo
        const fullNumber = cleaned ? `${selectedCountry.dialCode} ${cleaned}` : selectedCountry.dialCode;
        onChange(fullNumber);
    };

    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFocus = () => {
        setIsFocused(true);
        if (onFocus) onFocus();
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    return (
        <div className={`phone-input-field-container ${error ? 'error' : ''}`}>
            <div className="phone-input-wrapper">
                <div className="country-selector-wrapper">
                    <button
                        type="button"
                        className="country-selector-button"
                        onClick={() => setShowDropdown(!showDropdown)}
                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                        style={{ backgroundColor }}
                    >
                        <span className="country-flag">{selectedCountry.flag}</span>
                        <span className="country-code">{selectedCountry.dialCode}</span>
                        <i className={`fas fa-chevron-down ${showDropdown ? 'open' : ''}`}></i>
                    </button>
                    {showDropdown && (
                        <>
                            <div 
                                className="dropdown-overlay"
                                onClick={() => setShowDropdown(false)}
                            ></div>
                            <div className="country-dropdown">
                                <div className="country-list">
                                    {countryCodes.map((country) => (
                                        <div
                                            key={country.code}
                                            className={`country-option ${selectedCountry.code === country.code ? 'selected' : ''}`}
                                            onClick={() => handleCountrySelect(country)}
                                        >
                                            <span className="country-flag">{country.flag}</span>
                                            <span className="country-name">{country.name}</span>
                                            <span className="country-dial-code">{country.dialCode}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <input
                    ref={inputRef}
                    type="tel"
                    className="phone-input-field floating"
                    value={phoneNumber}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    required={required}
                    style={{ backgroundColor }}
                />
                {label && (
                    <label 
                        className="phone-input-label floating"
                        style={{ backgroundColor }}
                    >
                        {label}
                        {required && <span className="required-asterisk">*</span>}
                    </label>
                )}
            </div>
            {error && (
                <div className="error-message">
                    <i className="fas fa-exclamation-circle"></i>
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

export default PhoneInputField;

